import dayjs from 'dayjs'

import { GivingActionCard } from './GivingActionCard'
import type { GivingActionCardProps, GivingActionRow } from './GivingActionCard.types'
import { useGivingActionConfetti } from '@percent/workplace-giving/common/components/GivingActionConfetti/useGivingActionConfetti'
import { GivingActionCompletionModal } from '@percent/workplace-giving/common/components/GivingActionCompletionModal/GivingActionCompletionModal'
import { useGivingActionLogEvents } from '@percent/workplace-giving/common/components/LoggedDaysCalendar/useGivingActionLogEvents'
import { useGivingActionLogging } from '@percent/workplace-giving/common/hooks/useGivingActionLogging/useGivingActionLogging'

export type UseGivingActionInteractions = Readonly<{
  getCardProps: (row: GivingActionRow) => GivingActionCardProps
  renderCard: (row: GivingActionRow) => JSX.Element
  overlays: JSX.Element
}>

export type UseGivingActionInteractionsProps = Readonly<{
  showCampaignName?: boolean
}>

export const useGivingActionInteractions = ({
  showCampaignName
}: UseGivingActionInteractionsProps = {}): UseGivingActionInteractions => {
  const { fire, confetti } = useGivingActionConfetti()
  const {
    selectedRow,
    selectedDate,
    loggingRowId,
    completedRowIds,
    isLoggingAction,
    setSelectedDate,
    handleViewAction,
    handleLogAction,
    handleConfirmCompletion,
    handleCloseModal
  } = useGivingActionLogging({ onLogCreated: fire })
  const { loggedDates } = useGivingActionLogEvents({ row: selectedRow })

  const getCardProps = (row: GivingActionRow): GivingActionCardProps => ({
    row,
    onLogAction: handleLogAction,
    onViewAction: handleViewAction,
    isLogging: loggingRowId === row.id,
    justCompleted: completedRowIds.has(row.id),
    showCampaignName
  })

  const renderCard = (row: GivingActionRow) => <GivingActionCard {...getCardProps(row)} />

  const overlays = (
    <>
      <GivingActionCompletionModal
        open={!!selectedRow}
        row={selectedRow}
        selectedDate={selectedDate}
        onSelectedDateChange={setSelectedDate}
        onClose={handleCloseModal}
        onConfirm={handleConfirmCompletion}
        loading={isLoggingAction}
        loggedDates={loggedDates}
        minDate={selectedRow?.completableFrom ? dayjs(selectedRow.completableFrom).toDate() : undefined}
        maxDate={dayjs().endOf('day').toDate()}
      />
      {confetti}
    </>
  )

  return { getCardProps, renderCard, overlays }
}
